import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { 
  X, 
  Plus, 
  Users, 
  Clock, 
  DollarSign, 
  BookOpen, 
  Target,
  Settings,
  Trophy,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { 
  GroupCompetitionForm, 
  FormationPeriod, 
  TicketClass,
  CompetitionRules 
} from '../../types/group-competition.types';

interface GroupCompetitionCreatorProps {
  onClose: () => void;
  onGroupCreated: (group: GroupCompetitionForm) => void;
}

// Animations
const slideIn = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
  z-index: 1000;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  min-height: 90vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 15px;
`;

const TitleIcon = styled.span`
  font-size: 2.5rem;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 12px 20px;
  border-radius: 25px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  font-size: 14px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
`;

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 30px;
  animation: ${slideIn} 0.5s ease-out;
`;

const FormSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 25px;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.08);
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #ffd700;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 5px;
`;

const Input = styled.input`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 12px 16px;
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #ffd700;
    background: rgba(255, 255, 255, 0.15);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const TextArea = styled.textarea`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 12px 16px;
  color: white;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #ffd700;
    background: rgba(255, 255, 255, 0.15);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const Select = styled.select`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 12px 16px;
  color: white;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #ffd700;
  }
  
  option {
    background: #1e293b;
    color: white;
  }
`;

const TimeInputGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;
  margin-top: 10px;
`;

const TimeInput = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
`;

const TimeInputField = styled.input`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 8px;
  color: white;
  text-align: center;
  font-size: 0.9rem;
  width: 60px;
  
  &:focus {
    outline: none;
    border-color: #ffd700;
  }
`;

const TimeLabel = styled.span`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  font-weight: 600;
`;

const SubtopicInput = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const SubtopicInputField = styled.input`
  flex: 1;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 10px 16px;
  color: white;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: #ffd700;
  }
`;

const AddButton = styled.button`
  background: linear-gradient(45deg, #4caf50, #8bc34a);
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 5px 15px rgba(76, 175, 80, 0.4);
  }
`;

const RemoveButton = styled.button`
  background: rgba(244, 67, 54, 0.8);
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(244, 67, 54, 1);
    transform: scale(1.05);
  }
`;

const SubtopicTag = styled.div`
  background: rgba(255, 215, 0, 0.2);
  border: 1px solid rgba(255, 215, 0, 0.4);
  border-radius: 15px;
  padding: 5px 12px;
  color: #ffd700;
  font-size: 0.8rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RulesSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 25px;
`;

const RulesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
`;

const RuleItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const RuleLabel = styled.label`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: #ffd700;
`;

const NumberInput = styled.input`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 8px 12px;
  color: white;
  font-size: 0.9rem;
  width: 80px;
  text-align: center;
  
  &:focus {
    outline: none;
    border-color: #ffd700;
  }
`;

const CreateButton = styled.button`
  background: linear-gradient(45deg, #ffd700, #ffed4e);
  color: #1e293b;
  border: none;
  padding: 18px 40px;
  border-radius: 25px;
  font-size: 1.2rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  align-self: center;
  margin-top: 20px;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 30px rgba(255, 215, 0, 0.4);
  }
  
  &:disabled {
    background: rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.5);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 0.9rem;
  margin-top: 5px;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const SuccessMessage = styled.div`
  color: #4caf50;
  font-size: 0.9rem;
  margin-top: 5px;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const GroupCompetitionCreator: React.FC<GroupCompetitionCreatorProps> = ({ 
  onClose, 
  onGroupCreated 
}) => {
  const [form, setForm] = useState<GroupCompetitionForm>({
    name: '',
    description: '',
    category: '',
    subcategory: '',
    subtopics: [],
    stakeAmount: 0,
    ticketClass: 'third',
    formationPeriod: {
      months: 0,
      weeks: 0,
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0
    },
    rules: {
      maxWithdrawals: 3,
      gracePeriodMinutes: 5,
      tieBreaker: 'SPLIT_PRIZE',
      questionTimeLimit: 60,
      allowHints: true,
      allowCalculator: false,
      allowNotes: false,
      scoringSystem: {
        correctAnswer: 10,
        incorrectAnswer: 0,
        timeBonus: 2,
        difficultyMultiplier: 1.5,
        streakBonus: 5
      }
    }
  });

  const [newSubtopic, setNewSubtopic] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sample categories and subcategories
  const categories = [
    'Mathematics',
    'Science',
    'History',
    'Literature',
    'Geography',
    'Computer Science',
    'Languages',
    'Arts',
    'Physical Education',
    'Social Studies'
  ];

  const getSubcategories = (category: string) => {
    const subcategoryMap: Record<string, string[]> = {
      'Mathematics': ['Algebra', 'Geometry', 'Calculus', 'Statistics', 'Trigonometry', 'Number Theory'],
      'Science': ['Physics', 'Chemistry', 'Biology', 'Astronomy', 'Earth Science', 'Environmental Science'],
      'History': ['Ancient History', 'Medieval History', 'Modern History', 'World History', 'American History', 'European History'],
      'Literature': ['Poetry', 'Prose', 'Drama', 'Novels', 'Short Stories', 'Essays'],
      'Computer Science': ['Programming', 'Data Structures', 'Algorithms', 'Web Development', 'Machine Learning', 'Database Systems']
    };
    
    return subcategoryMap[category] || [];
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!form.name.trim()) {
      newErrors.name = 'Group name is required';
    }

    if (!form.category) {
      newErrors.category = 'Category is required';
    }

    if (!form.subcategory) {
      newErrors.subcategory = 'Subcategory is required';
    }

    if (form.subtopics.length === 0) {
      newErrors.subtopics = 'At least one subtopic is required';
    }

    if (form.stakeAmount <= 0) {
      newErrors.stakeAmount = 'Stake amount must be greater than 0';
    }

    const totalTime = Object.values(form.formationPeriod).reduce((sum, value) => sum + value, 0);
    if (totalTime === 0) {
      newErrors.formationPeriod = 'Formation period must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      onGroupCreated(form);
      
      // Show success message briefly before closing
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (error) {
      console.error('Error creating group:', error);
      setErrors({ submit: 'Failed to create group. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const addSubtopic = () => {
    if (newSubtopic.trim() && !form.subtopics.includes(newSubtopic.trim())) {
      setForm(prev => ({
        ...prev,
        subtopics: [...prev.subtopics, newSubtopic.trim()]
      }));
      setNewSubtopic('');
    }
  };

  const removeSubtopic = (index: number) => {
    setForm(prev => ({
      ...prev,
      subtopics: prev.subtopics.filter((_, i) => i !== index)
    }));
  };

  const updateFormationPeriod = (field: keyof FormationPeriod, value: number) => {
    setForm(prev => ({
      ...prev,
      formationPeriod: {
        ...prev.formationPeriod,
        [field]: Math.max(0, value)
      }
    }));
  };

  return (
    <ModalOverlay>
      <Header>
        <Title>
          <TitleIcon>🚢</TitleIcon>
          Create Group Competition
        </Title>
        <CloseButton onClick={onClose}>
          <X size={18} />
          Close
        </CloseButton>
      </Header>

      <ContentArea>
        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <FormSection>
            <SectionTitle>
              <BookOpen size={20} />
              Basic Information
            </SectionTitle>
            
            <FormGrid>
              <FormRow>
                <Label>Group Name *</Label>
                <Input
                  type="text"
                  placeholder="e.g., Math Gurus, Science Squad"
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                />
                {errors.name && <ErrorMessage><AlertCircle size={14} /> {errors.name}</ErrorMessage>}
              </FormRow>

              <FormRow>
                <Label>Description</Label>
                <TextArea
                  placeholder="Describe your group competition..."
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                />
              </FormRow>
            </FormGrid>
          </FormSection>

          {/* Category Selection */}
          <FormSection>
            <SectionTitle>
              <Target size={20} />
              Category & Topics
            </SectionTitle>
            
            <FormGrid>
              <FormRow>
                <Label>Category *</Label>
                <Select
                  value={form.category}
                  onChange={(e) => {
                    setForm(prev => ({ 
                      ...prev, 
                      category: e.target.value,
                      subcategory: '',
                      subtopics: []
                    }));
                  }}
                >
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </Select>
                {errors.category && <ErrorMessage><AlertCircle size={14} /> {errors.category}</ErrorMessage>}
              </FormRow>

              <FormRow>
                <Label>Subcategory *</Label>
                <Select
                  value={form.subcategory}
                  onChange={(e) => setForm(prev => ({ ...prev, subcategory: e.target.value }))}
                  disabled={!form.category}
                >
                  <option value="">Select Subcategory</option>
                  {form.category && getSubcategories(form.category).map(sub => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </Select>
                {errors.subcategory && <ErrorMessage><AlertCircle size={14} /> {errors.subcategory}</ErrorMessage>}
              </FormRow>
            </FormGrid>

            {/* Subtopic Management */}
            <div>
              <Label>Subtopics *</Label>
              <SubtopicInput>
                <SubtopicInputField
                  type="text"
                  placeholder="e.g., Laws of Indices, Quadratic Equations"
                  value={newSubtopic}
                  onChange={(e) => setNewSubtopic(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSubtopic())}
                />
                <AddButton type="button" onClick={addSubtopic}>
                  <Plus size={14} />
                  Add
                </AddButton>
              </SubtopicInput>
              
              {form.subtopics.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '15px' }}>
                  {form.subtopics.map((subtopic, index) => (
                    <SubtopicTag key={index}>
                      {subtopic}
                      <RemoveButton onClick={() => removeSubtopic(index)}>×</RemoveButton>
                    </SubtopicTag>
                  ))}
                </div>
              )}
              
              {errors.subtopics && <ErrorMessage><AlertCircle size={14} /> {errors.subtopics}</ErrorMessage>}
            </div>
          </FormSection>

          {/* Stake & Ticket Class */}
          <FormSection>
            <SectionTitle>
              <DollarSign size={20} />
              Stake & Ticket Class
            </SectionTitle>
            
            <FormGrid>
              <FormRow>
                <Label>Stake Amount (KES) *</Label>
                <Input
                  type="number"
                  placeholder="e.g., 500"
                  value={form.stakeAmount || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, stakeAmount: Number(e.target.value) }))}
                  min="1"
                />
                {errors.stakeAmount && <ErrorMessage><AlertCircle size={14} /> {errors.stakeAmount}</ErrorMessage>}
              </FormRow>

              <FormRow>
                <Label>Ticket Class</Label>
                <Select
                  value={form.ticketClass}
                  onChange={(e) => setForm(prev => ({ ...prev, ticketClass: e.target.value as TicketClass }))}
                >
                  <option value="third">Third Class (KES 1-499)</option>
                  <option value="second">Second Class (KES 500-999)</option>
                  <option value="first">First Class (KES 1000+)</option>
                </Select>
              </FormRow>
            </FormGrid>
          </FormSection>

          {/* Formation Period */}
          <FormSection>
            <SectionTitle>
              <Clock size={20} />
              Formation Period
            </SectionTitle>
            
            <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '20px' }}>
              Set how long users can join your group before the competition starts.
            </p>
            
            <TimeInputGroup>
              <TimeInput>
                <TimeInputField
                  type="number"
                  min="0"
                  max="12"
                  value={form.formationPeriod.months}
                  onChange={(e) => updateFormationPeriod('months', Number(e.target.value))}
                />
                <TimeLabel>Months</TimeLabel>
              </TimeInput>
              
              <TimeInput>
                <TimeInputField
                  type="number"
                  min="0"
                  max="52"
                  value={form.formationPeriod.weeks}
                  onChange={(e) => updateFormationPeriod('weeks', Number(e.target.value))}
                />
                <TimeLabel>Weeks</TimeLabel>
              </TimeInput>
              
              <TimeInput>
                <TimeInputField
                  type="number"
                  min="0"
                  max="31"
                  value={form.formationPeriod.days}
                  onChange={(e) => updateFormationPeriod('days', Number(e.target.value))}
                />
                <TimeLabel>Days</TimeLabel>
              </TimeInput>
              
              <TimeInput>
                <TimeInputField
                  type="number"
                  min="0"
                  max="23"
                  value={form.formationPeriod.hours}
                  onChange={(e) => updateFormationPeriod('hours', Number(e.target.value))}
                />
                <TimeLabel>Hours</TimeLabel>
              </TimeInput>
              
              <TimeInput>
                <TimeInputField
                  type="number"
                  min="0"
                  max="59"
                  value={form.formationPeriod.minutes}
                  onChange={(e) => updateFormationPeriod('minutes', Number(e.target.value))}
                />
                <TimeLabel>Minutes</TimeLabel>
              </TimeInput>
              
              <TimeInput>
                <TimeInputField
                  type="number"
                  min="0"
                  max="59"
                  value={form.formationPeriod.seconds}
                  onChange={(e) => updateFormationPeriod('seconds', Number(e.target.value))}
                />
                <TimeLabel>Seconds</TimeLabel>
              </TimeInput>
            </TimeInputGroup>
            
            {errors.formationPeriod && <ErrorMessage><AlertCircle size={14} /> {errors.formationPeriod}</ErrorMessage>}
          </FormSection>

          {/* Competition Rules */}
          <RulesSection>
            <SectionTitle>
              <Settings size={20} />
              Competition Rules
            </SectionTitle>
            
            <RulesGrid>
              <RuleItem>
                <Checkbox
                  type="checkbox"
                  id="allowHints"
                  checked={form.rules.allowHints}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    rules: { ...prev.rules, allowHints: e.target.checked }
                  }))}
                />
                <RuleLabel htmlFor="allowHints">Allow Hints</RuleLabel>
              </RuleItem>

              <RuleItem>
                <Checkbox
                  type="checkbox"
                  id="allowCalculator"
                  checked={form.rules.allowCalculator}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    rules: { ...prev.rules, allowCalculator: e.target.checked }
                  }))}
                />
                <RuleLabel htmlFor="allowCalculator">Allow Calculator</RuleLabel>
              </RuleItem>

              <RuleItem>
                <Checkbox
                  type="checkbox"
                  id="allowNotes"
                  checked={form.rules.allowNotes}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    rules: { ...prev.rules, allowNotes: e.target.checked }
                  }))}
                />
                <RuleLabel htmlFor="allowNotes">Allow Notes</RuleLabel>
              </RuleItem>

              <RuleItem>
                <RuleLabel>Question Time Limit (seconds):</RuleLabel>
                <NumberInput
                  type="number"
                  min="10"
                  max="300"
                  value={form.rules.questionTimeLimit}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    rules: { ...prev.rules, questionTimeLimit: Number(e.target.value) }
                  }))}
                />
              </RuleItem>

              <RuleItem>
                <RuleLabel>Max Withdrawals:</RuleLabel>
                <NumberInput
                  type="number"
                  min="1"
                  max="5"
                  value={form.rules.maxWithdrawals}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    rules: { ...prev.rules, maxWithdrawals: Number(e.target.value) }
                  }))}
                />
              </RuleItem>

              <RuleItem>
                <RuleLabel>Grace Period (minutes):</RuleLabel>
                <NumberInput
                  type="number"
                  min="1"
                  max="15"
                  value={form.rules.gracePeriodMinutes}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    rules: { ...prev.rules, gracePeriodMinutes: Number(e.target.value) }
                  }))}
                />
              </RuleItem>
            </RulesGrid>
          </RulesSection>

          {/* Submit Button */}
          <CreateButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creating Group...' : 'Create Group Competition'}
          </CreateButton>

          {errors.submit && <ErrorMessage><AlertCircle size={14} /> {errors.submit}</ErrorMessage>}
        </form>
      </ContentArea>
    </ModalOverlay>
  );
};

export default GroupCompetitionCreator;
